import { capitalize, Typography } from '@mui/material';
import { Workout } from '@strength/domain-workout';
import { formatDate } from '@strength/utils';
import { useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { DateRange } from '../../../../components/dates/RangeCalendar';
import { getLocale } from '../../../../utils/i18n';
import { PhaseSpan } from '../types';
import {
  computeLoadDays,
  computeLoadSpikeDays,
  LOAD_SPIKE_THRESHOLD_PERCENT,
  LoadDay,
} from './bowling-load-days';
import {
  DashedLineLegendItem,
  LegendCheckbox,
  LegendSwitch,
  renderPhaseBands,
  renderTodayReferenceLine,
  TodayLegendItem,
} from './ChartScaffold';
import { TooltipRow, TooltipShell } from './ChartTooltip';
import { computeTodayLine } from './chart-utils';

const SEVEN_DAY_COLOR = '#6927DA';
const TWENTY_EIGHT_DAY_COLOR = '#C01574';
const TEN_WEEK_COLOR = '#363F72';
const LOAD_SPIKE_COLOR = '#F04438';

type AverageLineVisibility = {
  sevenDay: boolean;
  twentyEightDay: boolean;
  tenWeek: boolean;
  loadSpike: boolean;
};

const DEFAULT_VISIBILITY: AverageLineVisibility = {
  sevenDay: true,
  twentyEightDay: true,
  tenWeek: false,
  loadSpike: false,
};

export const BowlingLoadRatioTrendChart = ({
  workouts,
  dateRange,
  phases,
  hasActualWorkoutIds,
}: {
  workouts: Workout[];
  dateRange: DateRange;
  phases: PhaseSpan[];
  /** workoutIds with a recorded actual — feeds the past-day exclusion rule in computeLoadDays. */
  hasActualWorkoutIds: Set<string>;
}) => {
  const { t } = useTranslation();

  // Use a unique ID for the chart instance to avoid conflicts with other charts
  const instanceId = useId().replace(/:/g, '');
  const bandGradientPurpleId = `bandGradientPurple-${instanceId}`;
  const bandGradientPinkId = `bandGradientPink-${instanceId}`;

  const [visibility, setVisibility] =
    useState<AverageLineVisibility>(DEFAULT_VISIBILITY);

  const data: LoadDay[] = useMemo(
    () =>
      computeLoadDays(workouts, dateRange, (workoutId) =>
        hasActualWorkoutIds.has(workoutId),
      ),
    [workouts, dateRange, hasActualWorkoutIds],
  );

  const { todayIso, showTodayLine } = computeTodayLine(data);
  const firstDay = data[0]?.day;
  const lastDay = data[data.length - 1]?.day;

  const loadSpikeDays = useMemo(
    () => (visibility.loadSpike ? computeLoadSpikeDays(data) : []),
    [data, visibility.loadSpike],
  );

  return (
    <div className="pr-10">
      <div className="flex flex-row items-center justify-between">
        <Typography variant="h4">{t('workloadRatioTrends')}</Typography>
        <Legend
          visibility={visibility}
          onToggle={(key, checked) =>
            setVisibility((prev) => ({ ...prev, [key]: checked }))
          }
          showTodayLine={showTodayLine}
          loadSpikeDays={loadSpikeDays}
        />
      </div>
      <ResponsiveContainer width="100%" height={350} className="mt-4">
        {/* AreaChart (not LineChart) so <defs> gradient is included in the SVG and the band fill renders */}
        <AreaChart data={data} margin={{ left: 5, bottom: 30 }}>
          <defs>
            <linearGradient
              id={bandGradientPurpleId}
              x1="0"
              y1="0"
              x2="0"
              y2="1"
              gradientUnits="objectBoundingBox"
            >
              <stop offset="0" stopColor={SEVEN_DAY_COLOR} stopOpacity={0.35} />
              <stop offset="1" stopColor={SEVEN_DAY_COLOR} stopOpacity={0.05} />
            </linearGradient>
            <linearGradient
              id={bandGradientPinkId}
              x1="0"
              y1="0"
              x2="0"
              y2="1"
              gradientUnits="objectBoundingBox"
            >
              <stop
                offset="0"
                stopColor={TWENTY_EIGHT_DAY_COLOR}
                stopOpacity={0.35}
              />
              <stop
                offset="1"
                stopColor={TWENTY_EIGHT_DAY_COLOR}
                stopOpacity={0.05}
              />
            </linearGradient>
          </defs>
          <XAxis
            dataKey="day"
            interval={6}
            tick={{ className: 'text-[10px]' }}
            tickFormatter={(value) =>
              formatDate(value, getLocale(), { month: 'short', day: 'numeric' })
            }
            label={{
              value: capitalize(t('day')),
              position: 'insideBottom',
              offset: -5,
              className: 'text-xs text-semibold',
            }}
          />
          <YAxis
            tick={{ className: 'text-[10px]' }}
            domain={[0, (dataMax: number) => Math.ceil(dataMax * 1.2)]}
            label={{
              value: capitalize(t('bowls')),
              angle: -90,
              position: 'insideLeft',
              className: 'text-xs text-semibold',
            }}
          />
          <Tooltip
            wrapperStyle={{
              width: 'auto',
              maxWidth: 'none',
              minWidth: '200px',
            }}
            content={({ active, payload }) => {
              const first = payload?.[0];
              if (!active || !first) return null;
              const row = first.payload as LoadDay;
              return (
                <ChartTooltip
                  row={row}
                  phases={phases}
                  visibility={visibility}
                  startsLoadSpike={loadSpikeDays.includes(row.day)}
                />
              );
            }}
          />
          <CartesianGrid stroke="#f5f5f5" />
          {renderPhaseBands({ phases, firstDay, lastDay })}
          {/*
            Band only (between the two lines), colored by which line is on top.
            Hidden whenever either underlying line is toggled off — the band
            visualizes the gap between them, so it's misleading with only one
            (or neither) of its two lines showing.
          */}
          <Area
            type="monotone"
            dataKey="bandPurpleRange"
            fill={`url(#${bandGradientPurpleId})`}
            fillOpacity={1}
            stroke="none"
            isAnimationActive={false}
            hide={!visibility.sevenDay || !visibility.twentyEightDay}
          />
          <Area
            type="monotone"
            dataKey="bandPinkRange"
            fill={`url(#${bandGradientPinkId})`}
            fillOpacity={1}
            stroke="none"
            isAnimationActive={false}
            hide={!visibility.sevenDay || !visibility.twentyEightDay}
          />

          <Line
            type="monotone"
            dataKey="sevenDayAvg"
            name={t('sevenDayAverage')}
            stroke={SEVEN_DAY_COLOR}
            dot={false}
            hide={!visibility.sevenDay}
          />
          <Line
            type="monotone"
            dataKey="twentyEightDayAvg"
            name={t('twentyEightDayAverage')}
            stroke={TWENTY_EIGHT_DAY_COLOR}
            dot={false}
            hide={!visibility.twentyEightDay}
          />
          <Line
            type="monotone"
            dataKey="tenWeekAvg"
            name={t('seventyDayChronicLoad')}
            stroke={TEN_WEEK_COLOR}
            dot={false}
            hide={!visibility.tenWeek}
          />
          {renderTodayReferenceLine({ todayIso, showTodayLine })}
          {loadSpikeDays.map((day) => (
            <ReferenceLine
              key={day}
              x={day}
              ifOverflow="visible"
              stroke={LOAD_SPIKE_COLOR}
              strokeWidth={2}
              strokeDasharray="4 4"
            />
          ))}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

const Legend = ({
  visibility,
  onToggle,
  showTodayLine,
  loadSpikeDays,
}: {
  visibility: AverageLineVisibility;
  onToggle: (key: keyof AverageLineVisibility, checked: boolean) => void;
  showTodayLine: boolean;
  loadSpikeDays: string[];
}) => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-row items-center gap-4">
      <LegendCheckbox
        color={SEVEN_DAY_COLOR}
        label={t('sevenDayAverage')}
        checked={visibility.sevenDay}
        onToggle={(checked) => onToggle('sevenDay', checked)}
      />
      <LegendCheckbox
        color={TWENTY_EIGHT_DAY_COLOR}
        label={t('twentyEightDayAverage')}
        checked={visibility.twentyEightDay}
        onToggle={(checked) => onToggle('twentyEightDay', checked)}
      />
      <LegendCheckbox
        color={TEN_WEEK_COLOR}
        label={t('seventyDayChronicLoad')}
        checked={visibility.tenWeek}
        onToggle={(checked) => onToggle('tenWeek', checked)}
      />
      <LegendSwitch
        label={t('loadSpikeOver50', {
          threshold: LOAD_SPIKE_THRESHOLD_PERCENT,
        })}
        checked={visibility.loadSpike}
        onToggle={(checked) => onToggle('loadSpike', checked)}
      />
      {visibility.loadSpike && loadSpikeDays.length > 0 ? (
        <DashedLineLegendItem
          color={LOAD_SPIKE_COLOR}
          label={t('loadSpikeOver50', {
            threshold: LOAD_SPIKE_THRESHOLD_PERCENT,
          })}
        />
      ) : null}
      {showTodayLine ? <TodayLegendItem /> : null}
    </div>
  );
};

/** Locale-aware percentage, rounded to the nearest whole number ("59%"). */
const formatPercent = (value: number) =>
  new Intl.NumberFormat(getLocale(), {
    style: 'percent',
    maximumFractionDigits: 0,
  }).format(value / 100);

/** Exported for testing — renders the tooltip content for one LoadDay. */
export const ChartTooltip = ({
  row,
  phases,
  visibility,
  startsLoadSpike = false,
}: {
  row: LoadDay;
  phases: PhaseSpan[];
  visibility: AverageLineVisibility;
  /** True when the hovered day starts a load-spike run — drives the
   * load-spike row above the 7-day average (the on-chart pill labels
   * overlapped, so the copy moved into the hover). */
  startsLoadSpike?: boolean;
}) => {
  const { t } = useTranslation();
  const rows = [
    visibility.sevenDay && {
      key: 'sevenDay',
      color: SEVEN_DAY_COLOR,
      label: t('sevenDayAverage'),
      value: row.sevenDayAvg.toFixed(1),
    },
    visibility.twentyEightDay && {
      key: 'twentyEightDay',
      color: TWENTY_EIGHT_DAY_COLOR,
      label: t('twentyEightDayAverage'),
      value: row.twentyEightDayAvg.toFixed(1),
    },
    visibility.tenWeek && {
      key: 'tenWeek',
      color: TEN_WEEK_COLOR,
      label: t('seventyDayChronicLoad'),
      value: row.tenWeekAvg.toFixed(1),
    },
  ].filter(Boolean) as Array<{
    key: string;
    color: string;
    label: string;
    value: string;
  }>;

  // Always shown (per Figma) — not tied to any legend visibility toggle.
  const percentDifferenceRow = {
    key: 'percentDifference',
    color: 'transparent',
    label: t('percentDifference'),
    subLabel:
      row.percentDifference !== null
        ? t('percentDifferenceFormula', {
            sevenDayAvg: row.sevenDayAvg.toFixed(1),
            twentyEightDayAvg: row.twentyEightDayAvg.toFixed(1),
          })
        : undefined,
    value:
      row.percentDifference !== null
        ? formatPercent(row.percentDifference)
        : t('percentDifferenceMissing'),
  };

  return (
    <TooltipShell day={row.day} phases={phases}>
      {visibility.loadSpike && startsLoadSpike ? (
        <TooltipRow
          color={LOAD_SPIKE_COLOR}
          label={t('loadSpikeOver50', {
            threshold: LOAD_SPIKE_THRESHOLD_PERCENT,
          })}
          value=""
          last={false}
        />
      ) : null}
      {rows.map((r) => (
        <TooltipRow
          key={r.key}
          color={r.color}
          label={r.label}
          value={r.value}
          last={false}
        />
      ))}
      <TooltipRow
        color={percentDifferenceRow.color}
        label={percentDifferenceRow.label}
        subLabel={percentDifferenceRow.subLabel}
        value={percentDifferenceRow.value}
        last
      />
    </TooltipShell>
  );
};
